import { tr } from "../i18n/LanguageContext";
import React, { useState, useEffect } from "react";
import { useNavigate } from "../i18n/router";
import { useAuth } from "../contexts/AuthContext";
import { Bell, MessageCircle, UserPlus, Calendar, Star, Loader2, CheckCheck } from "lucide-react";
import { formatNotificationTime } from "../lib/time";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  new_message: MessageCircle,
  new_follower: UserPlus,
  new_event: Calendar,
  review_request: Star,
  event_reminder: Bell,
};

const NotificationsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login?redirect=/notifications");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/notifications`, { credentials: "include" });
        if (res.ok) setNotifications(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const markAllRead = async () => {
    await fetch(`${BACKEND_URL}/api/notifications/mark-read`, { method: "POST", credentials: "include" });
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>;
  }

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Bell className="h-7 w-7" style={{ color: "var(--eunoia-accent)" }} />
            <h1 className="eunoia-title-large">{tr("Уведомления")}</h1>
            {unread > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: "var(--eunoia-accent)" }}>{unread}</span>
            )}
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="eunoia-btn-secondary text-sm flex items-center gap-1.5">
              <CheckCheck className="h-4 w-4" /> {tr("Прочитать все")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>
        ) : notifications.length === 0 ? (
          <div className="eunoia-surface p-12 text-center">
            <Bell className="h-12 w-12 mx-auto mb-4" style={{ color: "var(--eunoia-whisper)" }} />
            <p className="eunoia-body">{tr("Нет уведомлений")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => {
              const Icon = iconMap[n.type] || Bell;
              return (
                <div
                  key={n.id}
                  className={`eunoia-surface p-4 flex items-start gap-3 cursor-pointer hover:bg-black/[0.02] transition-colors ${!n.read ? 'border-l-4' : ''}`}
                  style={{ borderLeftColor: !n.read ? 'var(--eunoia-accent)' : undefined }}
                  onClick={() => n.link && navigate(n.link)}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
                    <Icon className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: "var(--eunoia-deep)" }}>{n.title}</p>
                    <p className="text-sm mt-0.5" style={{ color: "var(--eunoia-soft)" }}>{n.body}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--eunoia-soft)" }}>
                      {formatNotificationTime(n.createdAt)}
                    </p>
                  </div>
                  {!n.read && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: "var(--eunoia-accent)" }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
